import { createSlice, createAsyncThunk } from "@reduxjs/toolkit/";
import { clearCookie, getCookie, setAccessCookie, setCookie } from "../api/apiHelper";
import {parseJwt} from "../api/apiHelper"
import config from "../../config"
const LOGIN_URL = `${config.REACT_APP_LOGIN}/auth/login/`;
const REFRESH_URL = `${config.REACT_APP_LOGIN}/auth/token/refresh/`;

export const loginUser = createAsyncThunk("login/loginUser", async (body) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },

    body: JSON.stringify(body),
  };
  const response = await fetch(LOGIN_URL, requestOptions);

  return response.json();
});

// The token's company, team and groups were written at login and never again, so an
// installer linked to a company after signing in was refused at the scan ("Your account
// has no installer company") until a full logout (stress test 22/10, T8). On start-up the
// app asks Userport for a token rebuilt from the user's current details. If Userport refuses
// the refresh token (401) the session is over; any other failure leaves it as it is.
//
// Route guards wait for this answer (sessionChecked), so they decide on the refreshed
// roles: a role added after login used to be refused by the guard before the refresh came
// back (TF5). The timeout keeps a slow Userport from holding the app.
const REFRESH_TIMEOUT_MS = 4000;

export const refreshSession = createAsyncThunk("login/refreshSession", async (_arg, { rejectWithValue }) => {
  const refresh = getCookie("refresh");
  if (!refresh) return rejectWithValue("no session");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    const response = await fetch(REFRESH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
      signal: controller.signal,
    });
    if (!response.ok) return rejectWithValue(response.status);
    return await response.json();
  } catch (error) {
    return rejectWithValue("unreachable");
  } finally {
    clearTimeout(timer);
  }
});

const initialState = getCookie("access")
  ? {
      token: `Bearer ${getCookie("access")}`,
      isLoggedIn: true,
      isLoginError: false,
      user_groups: parseJwt(getCookie("access")).user_groups,
      user: parseJwt(getCookie("access")).user,
      sessionChecked: !getCookie("refresh"),
    }
  : { token: "",
      isLoggedIn: false,
      isLoginError: false,
      user_groups: [],
      user: "",
      sessionChecked: true,
    };

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // The previous user's groups and name went with them only on a reload, so the next user
    // in the same tab briefly ran as them (P3). The cached API data is reset in store.js.
    logout: (state) => {
      state.isLoggedIn = false;
      state.token = "";
      state.user = "";
      state.user_groups = [];
      clearCookie();
    },
  },
  extraReducers(builder) {
    builder.addCase(refreshSession.fulfilled, (state, action) => {
      const response = action.payload;
      state.sessionChecked = true;
      if (!response.access) return;
      setAccessCookie(response.access);
      state.token = response.access;
      state.user_groups = parseJwt(response.access).user_groups;
      state.user = parseJwt(response.access).user;
    });
    builder.addCase(refreshSession.rejected, (state, action) => {
      state.sessionChecked = true;
      // Userport refused the refresh token: it has expired or the user is gone, so the
      // session is over (R4). An unreachable Userport still leaves the session as it is.
      if (action.payload === 401) {
        state.isLoggedIn = false;
        state.token = "";
        state.user_groups = [];
        state.user = "";
        clearCookie();
      }
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      let response = action.payload;
      if (response.access) {
        state.token = response.access;
        state.isLoggedIn = true;
        setCookie(response);
        state.user_groups = parseJwt(response.access).user_groups;
        state.user = parseJwt(response.access).user;
        state.isLoginError = false;
        state.sessionChecked = true;
      } else {
        state.isLoginError = true;
      }
    });
  },
});

export const getToken = (state) => state.auth.token;
export const checkIsLoggedIn = (state) => state.auth.isLoggedIn;
export const checkIsLoginError = (state) => state.auth.isLoginError;
export const getUserGroups = (state) => state.auth.user_groups;
export const checkSessionChecked = (state) => state.auth.sessionChecked;
export const getUserName = (state) => state.auth.user;
export const { logout } = authSlice.actions;

export default authSlice.reducer;
